import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { FilterContentItem, FilterModalState, FilterModalType, FreeColumnFilterContentItem, isDateRangeFilterType } from './filterModal';
import { i18n } from '@/i18n';
import { FilterModalViewModel } from './filterModalViewModel';
import { simpleCopy } from '@/utils/deepCopy';

const t = i18n.t;
const vm = new FilterModalViewModel();

export const useFilterModalStore = defineStore('filterModal', () => {
  const state = reactive<FilterModalState>({
    filterItems: [],
    freeColumnFilterItems: [],
    currentFilterType: undefined,
  });

  /**
   * リセット
   */
  const $reset = () => {
    state.filterItems = [];
    state.freeColumnFilterItems = [];
    state.currentFilterType = undefined;
  };

  /**
   * 適用されたフィルターコンテンツ
   */
  const appliedFilterContent = computed((): FilterContentItem[] => {
    const appliedItems = state.filterItems.filter((item) => {
      if (item.filterType === 'LAST_CHECKED_AT') {
        return item.selectedValue.length > 0 || item.includeNull;
      }
      if (isDateRangeFilterType(item.filterType)) {
        const hasRange = item.selectedValue.length > 0 && (item.selectedValue[0] || item.selectedValue[1]);
        return hasRange || item.includeNull;
      }
      return item.selectedValue.length > 0;
    });

    return simpleCopy(appliedItems);
  });

  const appliedFreeColumnFilterContent = computed((): FreeColumnFilterContentItem[] => {
    return simpleCopy(
      state.freeColumnFilterItems.filter((item) => {
        return item.selectedValue.length > 0;
      })
    );
  });

  /**
   * 通常のフィルター設定をリセットする
   */
  const resetFilterItems = () => {
    state.filterItems.forEach((item) => {
      item.selectedValue = [];
      if (item.includeNull !== undefined) {
        item.includeNull = false;
      }
    });
  };

  /**
   * 全ての設定をリセットする
   */
  const resetAllSetting = () => {
    resetFilterItems();
    state.freeColumnFilterItems.forEach((item) => {
      item.selectedValue = [];
    });
  };

  /**
   * フィルターアイテムを設定する
   * @param filterModalType - フィルターモーダルのタイプ
   */
  const setFilterItems = (filterModalType: FilterModalType) => {
    state.filterItems = simpleCopy(vm.useFilterItems(filterModalType));
  };

  /**
   * フィルターアイテムを設定する
   * @param items - フィルターアイテム
   */
  const updateFilterItems = (items?: FilterContentItem[]) => {
    if (!items) return;

    const nextItems = simpleCopy(items);

    resetFilterItems();

    // 同じfilterTypeについて、selectedValueを上書きする
    nextItems.forEach((item) => {
      const target = state.filterItems.find((filterItem) => filterItem.filterType === item.filterType);
      if (target) {
        target.selectedValue = item.selectedValue;
        if (item.includeNull !== undefined) {
          target.includeNull = item.includeNull;
        }
      }
    });
  };

  /**
   * 自由欄フィルターアイテムを設定する
   * @param items - 自由欄フィルターアイテム
   */
  const updateFreeColumnFilterItems = (items?: FreeColumnFilterContentItem[]) => {
    state.freeColumnFilterItems = simpleCopy(items ?? []);
  };

  /**
   * 指定したフィルタータイプの自由欄フィルターアイテムを更新する
   * @param filterType - 自由欄フィルタータイプ
   * @param items - 自由欄フィルターアイテム
   */
  const updateFreeColumnFilterItemsByFilterType = (
    filterType: FreeColumnFilterContentItem['filterType'],
    items: FreeColumnFilterContentItem[]
  ) => {
    state.freeColumnFilterItems = [...state.freeColumnFilterItems.filter((item) => item.filterType !== filterType), ...simpleCopy(items)];
  };

  return {
    state,
    $reset,
    appliedFilterContent,
    appliedFreeColumnFilterContent,
    resetAllSetting,
    setFilterItems,
    updateFilterItems,
    updateFreeColumnFilterItems,
    updateFreeColumnFilterItemsByFilterType,
  };
});
